import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Typography,
  IconButton,
  MenuItem,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  InputLabel,
  Grid,
  DialogTitle,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useUpdateProgressStatus } from '../../hooks/useCandidate';
import { GET_MANAGERS, GET_HR_CONTACTS, SEND_ROUND_EMAIL } from '../../api';
import CcEditor from './CcEditor';

// ---- Design tokens (shared look with the Schedule Interview modal) ----
const ACCENT = '#2f6bff';
const GRADIENT_BTN = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '12px',
  px: 3,
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
// One outlined-field look reused by every text field, select and picker.
const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&:hover .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
    borderColor: '#c7d2fe',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: ACCENT,
    borderWidth: '1.5px',
  },
};
// The app's fixed admin header sits at z-index 1301, above MUI's default modal
// (1300) — lift the dialog to modal+2 so its title isn't hidden, and its menus
// / date poppers to modal+3 so they stay above the lifted dialog.
const DIALOG_ZINDEX = (theme) => theme.zIndex.modal + 2;
const OVERLAY_ZINDEX = (theme) => theme.zIndex.modal + 3;
const SELECT_MENU_PROPS = { MenuProps: { sx: { zIndex: OVERLAY_ZINDEX } } };
const PICKER_SLOTS = {
  textField: { fullWidth: true, size: 'small', sx: FIELD_SX },
  popper: { sx: { zIndex: OVERLAY_ZINDEX } },
};
// Interview-time dropdowns: 12-hour hours ('01'..'12') and 5-minute steps ('00'..'55').
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

// Small uppercase section heading that groups related fields.
const SectionTitle = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: '12px',
      fontWeight: 800,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#9ca3af',
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

// One label style for every field, rendered above the input.
const Label = (props) => (
  <InputLabel
    {...props}
    sx={{ mb: '6px', display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', ...props.sx }}
  />
);

export default function ProgressModel({ selectedCan, open, setOpen }) {
  const handleClose = () => setOpen(false);
  const { mutate: updateProgressStatus } = useUpdateProgressStatus();
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    phone_number: '',
    email_address: '',
    interviewer_name: '',
    manual_interviewer_name: '',
    resume: '',
    portfolioLink: '',
  });
  const [selectedDateValue, setSelectedDateValue] = useState(null); // dayjs for the picker
  const [, setSelectedTimeValue] = useState(null); // only the reset setter is needed
  const [status, setStatus] = useState('');
  const [nextRound, setNextRound] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [, setSubmittedData] = useState(null);
  const [interviewemail, setInterviewemail] = useState('');
  const [managers, setManagers] = useState([]);
  const [tm, setTm] = useState('');
  const [roundNumber, setRoundNumber] = useState(2); // Default to round 2 if moving forward
  const [meetingLink, setMeetingLink] = useState('');
  const [hrContacts, setHrContacts] = useState([]);
  const [hrCcName, setHrCcName] = useState('');
  const [hrCcEmail, setHrCcEmail] = useState('');

  // Interview time is chosen via three dropdowns (Hour / Minute / AM-PM) that feed
  // the dayjs value + `tm` string through handleTimeChange, so the submit payload
  // is unchanged.
  const [hourSel, setHourSel] = useState('');
  const [minuteSel, setMinuteSel] = useState('');
  const [meridiemSel, setMeridiemSel] = useState('');

  // Preview-before-send for the "moved to next round" email.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null); // {to, cc, subject, html}
  const [ccList, setCcList] = useState([]); // editable Cc for the round email
  const [previewAppName, setPreviewAppName] = useState('');
  const [sending, setSending] = useState(false);

  // Load reporting managers + HR contacts for the dropdowns
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [mgrRes, hrRes] = await Promise.allSettled([GET_MANAGERS(), GET_HR_CONTACTS()]);
        if (!active) return;
        if (mgrRes.status === 'fulfilled') {
          const m = mgrRes.value;
          setManagers(Array.isArray(m) ? m : m?.results || m?.managers || []);
        }
        if (hrRes.status === 'fulfilled') {
          const h = hrRes.value;
          setHrContacts(Array.isArray(h) ? h : h?.results || h?.hr_contacts || h?.data || []);
        }
      } catch (err) {
        console.error('Failed to load managers / HR contacts', err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleTimeChange = (newTime) => {
    setSelectedTimeValue(newTime);
    setTm(newTime ? newTime.format('HH:mm:ss') : '');
  };

  const applyTimeParts = (h, m, mer) => {
    if (h && m !== '' && mer) {
      let hour24 = parseInt(h, 10) % 12; // 12 -> 0
      if (mer === 'PM') hour24 += 12;
      handleTimeChange(dayjs().hour(hour24).minute(parseInt(m, 10)).second(0));
    } else {
      handleTimeChange(null);
    }
  };

  useEffect(() => {
    if (selectedCan) {
      setFormData({
        name: selectedCan.name,
        role: selectedCan.role,
        phone_number: selectedCan.phone_number,
        email_address: selectedCan.email_address,
        interviewer_name: selectedCan.interviewer_name || '',
        manual_interviewer_name: '',
        resume: selectedCan.resume,
        portfolioLink: selectedCan.portfolio_link,
      });
      setStatus('');
      setNextRound('');
      setRejectionReason('');
      setInterviewemail(selectedCan.interviewemail || '');
      setHrCcName(selectedCan.hr_cc_name || '');
      setHrCcEmail(selectedCan.hr_cc_email || '');
      // Default the meeting link to the candidate's existing / previous link so
      // HR doesn't have to re-paste it each round (still editable).
      setMeetingLink(selectedCan.manual_meeting_link || selectedCan.zoom_meeting_link || '');
      setSelectedDateValue(null);
      setSelectedTimeValue(null);
      setTm('');
      setHourSel('');
      setMinuteSel('');
      setMeridiemSel('');
      setPreviewOpen(false);
      setPreviewData(null);
      setCcList([]);
      setPreviewAppName('');
      setSending(false);
    }
  }, [selectedCan]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
    setNextRound(''); // Reset next round when changing status
    setRejectionReason(''); // Reset rejection reason when changing status
  };

  const handleNextRoundChange = (event) => setNextRound(event.target.value);
  const handleDateChange = (newDate) => setSelectedDateValue(newDate);
  const handleInterviewEmail = (e) => setInterviewemail(e.target.value);

  // Pick a reporting manager as interviewer and auto-fill their email
  const handleInterviewerSelect = (value) => {
    setFormData((prev) => ({ ...prev, interviewer_name: value }));
    if (value === 'manual') {
      setInterviewemail('');
    } else {
      const mgr = managers.find((m) => m.name === value);
      setInterviewemail(mgr?.email || '');
    }
  };

  const handleSubmit = () => {
    if (!status) {
      alert('Please select a status.');
      return;
    }
    const interviewername =
      formData.interviewer_name === 'manual'
        ? formData.manual_interviewer_name
        : formData.interviewer_name;
    const interviewDate = selectedDateValue ? selectedDateValue.format('YYYY-MM-DD') : '';

    if (
      status === 'move_to_next_round' &&
      (!interviewername || !interviewemail || !interviewDate)
    ) {
      alert(
        'For "Move To Next Round", please fill in Interviewer, Interview Email and Interview Date.'
      );
      return;
    }

    const submissionData = {
      applicationName: formData.name,
      interviewername,
      status,
      nextRound: status === 'move_to_next_round' ? nextRound : undefined,
      rejectionReason: status === 'rejected' ? rejectionReason : undefined,
    };

    setSubmittedData(submissionData);

    updateProgressStatus(
      {
        applicationName: submissionData.applicationName,
        status: submissionData.status,
        next_round: submissionData.nextRound,
        rejection_reason: submissionData.rejectionReason,
        interviewer_name: submissionData.interviewername,
        interviewer_email: interviewemail,
        interview_date: interviewDate,
        interview_time: tm,
        round_number: status === 'move_to_next_round' ? roundNumber : undefined,
        meeting_link: meetingLink,
        hr_cc_name: hrCcName,
        hr_cc_email: hrCcEmail,
      },
      {
        onSuccess: (data) => {
          // Move-to-next-round: show the email preview, then HR sends it.
          if (status === 'move_to_next_round') {
            if (data && data.email_preview) {
              setPreviewData(data.email_preview);
              setCcList(data.email_preview.cc || []);
              setPreviewAppName(submissionData.applicationName);
              setOpen(false); // close the status modal; the preview opens on top
              setPreviewOpen(true);
            } else {
              // Preview couldn't be built — don't close silently.
              alert('Status updated, but the round email could not be prepared. Please retry.');
              handleClose();
            }
            return;
          }
          // Selected / rejected: the mail is auto-sent — flag if it failed.
          if (data && data.email_sent === false) {
            alert('Status updated — but the notification email could not be sent.');
          }
          handleClose();
        },
        onError: () => alert('Error while updating status. Please try again.'),
      }
    );
  };

  const handleSkipPreview = () => {
    setPreviewOpen(false);
    setPreviewData(null);
  };

  const handleSendRoundEmail = async () => {
    if (!previewAppName) return;
    setSending(true);
    try {
      const res = await SEND_ROUND_EMAIL(previewAppName, ccList);
      if (res && res.email_sent) {
        alert('Email sent to the candidate.');
        // Close only on a confirmed send; on failure keep the preview open so
        // HR can retry (the candidate has already advanced to the next round).
        setPreviewOpen(false);
        setPreviewData(null);
      } else {
        alert('Could not send the email. Please try again.');
      }
    } catch (e) {
      alert(e?.response?.data?.error || 'Could not send the email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const isManualInterviewer = formData.interviewer_name === 'manual';
  const isMovingForward = status === 'move_to_next_round';

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{ zIndex: DIALOG_ZINDEX }}
      PaperProps={{ sx: { borderRadius: '22px' } }}
      aria-labelledby="update-status-title"
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2.5, sm: 3.5 },
          pt: 3,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: '#eef3ff',
              color: ACCENT,
              flexShrink: 0,
            }}
          >
            <HowToRegIcon />
          </Box>
          <Box>
            <Typography
              id="update-status-title"
              sx={{ fontWeight: 800, fontSize: '22px', color: '#111827', lineHeight: 1.2 }}
            >
              Update Candidate Status
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>
              Record the outcome of this interview round.
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#9ca3af' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
        {/* ---- Candidate details ---- */}
        <SectionTitle>Candidate Details</SectionTitle>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Label>Candidate Name</Label>
            <TextField
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              placeholder="Enter candidate's full name"
              fullWidth
              size="small"
              sx={FIELD_SX}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Label>Role / Position</Label>
            <TextField
              name="role"
              value={formData.role || ''}
              onChange={handleInputChange}
              placeholder="Enter role / position"
              fullWidth
              size="small"
              sx={FIELD_SX}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Label>Candidate Phone Number</Label>
            <TextField
              name="phone_number"
              value={formData.phone_number || ''}
              onChange={handleInputChange}
              placeholder="Enter candidate's phone number"
              fullWidth
              size="small"
              sx={FIELD_SX}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Label>Candidate Email</Label>
            <TextField
              name="email_address"
              value={formData.email_address || ''}
              onChange={handleInputChange}
              placeholder="Enter candidate's email"
              fullWidth
              size="small"
              sx={FIELD_SX}
            />
          </Grid>
        </Grid>

        {/* ---- Decision ---- */}
        <SectionTitle sx={{ mt: 3.5 }}>Decision &amp; Notification</SectionTitle>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Label>Status</Label>
            <TextField
              select
              value={status}
              onChange={handleStatusChange}
              fullWidth
              size="small"
              sx={FIELD_SX}
              SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
            >
              <MenuItem value="" disabled>Select status</MenuItem>
              <MenuItem value="selected">Selected</MenuItem>
              <MenuItem value="move_to_next_round">Move To Next Round</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Label>Interviewer Name</Label>
            <TextField
              select
              value={formData.interviewer_name || ''}
              onChange={(e) => handleInterviewerSelect(e.target.value)}
              fullWidth
              size="small"
              sx={FIELD_SX}
              SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
            >
              <MenuItem value="">Select Interviewer</MenuItem>
              {managers.map((mgr) => (
                <MenuItem key={mgr.id} value={mgr.name}>
                  {mgr.name}
                </MenuItem>
              ))}
              <MenuItem value="manual">Enter Manually</MenuItem>
            </TextField>
          </Grid>

          {isManualInterviewer && (
            <Grid item xs={12} sm={6}>
              <Label>Interviewer Name (Manual)</Label>
              <TextField
                name="manual_interviewer_name"
                value={formData.manual_interviewer_name || ''}
                onChange={handleInputChange}
                placeholder="Enter interviewer name"
                fullWidth
                size="small"
                sx={FIELD_SX}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Label>Interviewer Email</Label>
            <TextField
              value={interviewemail}
              onChange={handleInterviewEmail}
              disabled={formData.interviewer_name !== '' && formData.interviewer_name !== 'manual'}
              placeholder="Interviewer email"
              fullWidth
              size="small"
              sx={FIELD_SX}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Label>HR CC Name</Label>
            <TextField
              select
              value={hrCcName || ''}
              onChange={(e) => {
                const value = e.target.value;
                setHrCcName(value);
                const contact = hrContacts.find((c) => c.name === value);
                setHrCcEmail(contact?.email || '');
              }}
              fullWidth
              size="small"
              sx={FIELD_SX}
              SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
            >
              <MenuItem value="">Select HR</MenuItem>
              {hrContacts.map((contact) => (
                <MenuItem key={contact.id} value={contact.name}>
                  {contact.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Label>HR CC Email</Label>
            <TextField
              value={hrCcEmail}
              onChange={(e) => setHrCcEmail(e.target.value)}
              placeholder="HR email to keep in Cc"
              fullWidth
              size="small"
              sx={FIELD_SX}
            />
          </Grid>
        </Grid>

        {/* ---- Move-to-next-round details ---- */}
        {isMovingForward && (
          <>
            <SectionTitle sx={{ mt: 3.5 }}>Next Round Details</SectionTitle>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Label>Next Round</Label>
                <TextField
                  select
                  value={nextRound}
                  onChange={handleNextRoundChange}
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                  SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
                >
                  <MenuItem value="" disabled>Select Stage</MenuItem>
                  <MenuItem value="hr_interview">HR Interview</MenuItem>
                  <MenuItem value="assessment">Assessment</MenuItem>
                  <MenuItem value="technical_interview">Technical Interview</MenuItem>
                  <MenuItem value="final_round">Final Round</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>Round Number</Label>
                <TextField
                  select
                  value={roundNumber}
                  onChange={(e) => setRoundNumber(e.target.value)}
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                  SelectProps={SELECT_MENU_PROPS}
                >
                  <MenuItem value={1}>1st Round</MenuItem>
                  <MenuItem value={2}>2nd Round</MenuItem>
                  <MenuItem value={3}>3rd Round</MenuItem>
                  <MenuItem value={4}>4th Round</MenuItem>
                  <MenuItem value={5}>Final Round</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>Interview Date</Label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={selectedDateValue}
                    onChange={handleDateChange}
                    slotProps={PICKER_SLOTS}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>Interview Time</Label>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    select
                    value={hourSel}
                    onChange={(e) => {
                      setHourSel(e.target.value);
                      applyTimeParts(e.target.value, minuteSel, meridiemSel);
                    }}
                    fullWidth
                    size="small"
                    sx={FIELD_SX}
                    SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="" disabled>HH</MenuItem>
                    {HOUR_OPTIONS.map((h) => (
                      <MenuItem key={h} value={h}>{h}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    value={minuteSel}
                    onChange={(e) => {
                      setMinuteSel(e.target.value);
                      applyTimeParts(hourSel, e.target.value, meridiemSel);
                    }}
                    fullWidth
                    size="small"
                    sx={FIELD_SX}
                    SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="" disabled>MM</MenuItem>
                    {MINUTE_OPTIONS.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    value={meridiemSel}
                    onChange={(e) => {
                      setMeridiemSel(e.target.value);
                      applyTimeParts(hourSel, minuteSel, e.target.value);
                    }}
                    fullWidth
                    size="small"
                    sx={FIELD_SX}
                    SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="" disabled>AM/PM</MenuItem>
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                  </TextField>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Label>Meeting Link (Manual)</Label>
                <TextField
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  placeholder="Google Meet or other link (optional)"
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* ---- Rejection reason ---- */}
        {status === 'rejected' && (
          <>
            <SectionTitle sx={{ mt: 3.5 }}>Rejection</SectionTitle>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Label>Rejection Reason</Label>
                <TextField
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Write the rejection reason here"
                  fullWidth
                  multiline
                  minRows={2}
                  sx={FIELD_SX}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2 }}>
        <Button
          onClick={handleClose}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<CheckCircleOutlineIcon />}
          onClick={handleSubmit}
          sx={GRADIENT_BTN}
        >
          Update Status
        </Button>
      </DialogActions>
    </Dialog>

    {/* Email preview — shown after moving to the next round; HR sends only
        after reviewing it. */}
    <Dialog
      open={previewOpen}
      onClose={handleSkipPreview}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: DIALOG_ZINDEX }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Next-Round Email Preview</DialogTitle>
      <DialogContent dividers>
        {previewData ? (
          <>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>To:</strong> {(previewData.to || []).join(', ') || '—'}
            </Typography>
            <Box sx={{ mb: 0.75 }}>
              <CcEditor value={ccList} onChange={setCcList} accent={ACCENT} />
            </Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Subject:</strong> {previewData.subject}
            </Typography>
            {(!previewData.to || previewData.to.length === 0) && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                No recipient email address — sending will fail.
              </Alert>
            )}
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
              <iframe
                title="round-email-preview"
                srcDoc={previewData.html || ''}
                style={{ width: '100%', height: '460px', border: 'none' }}
              />
            </Box>
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleSkipPreview} color="inherit" disabled={sending}>
          Skip
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSendRoundEmail}
          disabled={sending || !previewData || !(previewData.to && previewData.to.length)}
          sx={GRADIENT_BTN}
        >
          {sending ? 'Sending…' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
